import { create } from 'zustand'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '../config/firebase'

export interface EventTypeStats {
  total: number
  byTarget?: Record<string, number>
  byType?: Record<string, number>
  terms?: Record<string, number>
}

export interface EventStats {
  byEventType: Record<string, EventTypeStats>
  lastUpdatedAt: Date | null
}

interface EventStatsState {
  eventStats: EventStats | null
  loading: boolean
  error: string | null
  fetchEventStats: () => Promise<void>
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  cta_click: 'CTA 點擊',
  resource_click: '外部連結點擊',
  community_link_click: '社群連結點擊',
  search: '搜尋',
  tab_switch: 'Tab 切換',
  category_expand: '分類展開',
  survey_start: '量表開始',
  survey_submit: '量表送出',
  survey_abandon: '量表放棄',
  discussion_open: '討論開啟',
  discussion_like: '討論按讚',
  discussion_reply: '討論回覆',
  discussion_create: '討論發起',
  logout: '登出',
  login_method: '登入方式',
  video_play: '影片播放',
  video_pause: '影片暫停',
  video_complete: '影片完成',
  video_fullscreen: '影片全螢幕',
}

export function getEventTypeLabel(key: string): string {
  return EVENT_TYPE_LABELS[key] ?? key
}

const CTA_TARGET_LABELS: Record<string, string> = {
  login_email: 'Email 登入',
  qr_login: 'QR Code 登入',
  register_link: '建立新帳號',
  forgot_password: '忘記密碼',
  save_profile: '儲存個人資料',
  home_grid_survey: '首頁 → 填寫問卷',
  home_grid_discussion: '首頁 → 討論區',
  home_grid_resources: '首頁 → 資源專區',
  home_grid_history: '首頁 → 問卷紀錄',
  survey_overall_summary: '綜合總覽',
  discussion_create: '發起討論',
  account_logout: '登出',
  account_edit_profile: '編輯個人資料',
}

export function getCtaTargetLabel(key: string): string {
  return CTA_TARGET_LABELS[key] ?? key
}

const HIDDEN_EVENT_TYPES = new Set(['note_save', 'note_delete'])

export const useEventStatsStore = create<EventStatsState>((set) => ({
  eventStats: null,
  loading: false,
  error: null,

  fetchEventStats: async () => {
    set({ loading: true, error: null })
    try {
      const snap = await getDoc(doc(db, 'analytics', 'eventStats'))
      if (!snap.exists()) {
        set({ eventStats: { byEventType: {}, lastUpdatedAt: null }, loading: false })
        return
      }
      const data = snap.data()

      // Firestore 上の flat key（例: byEventType.cta_click.total）を展開
      const byEventType: Record<string, EventTypeStats> = {}
      for (const [rawKey, value] of Object.entries(data)) {
        if (rawKey === 'lastUpdatedAt') continue
        const parts = rawKey.split('.')
        if (parts[0] === 'byEventType' && parts.length >= 3) {
          const eventType = parts[1]
          if (HIDDEN_EVENT_TYPES.has(eventType)) continue
          const field = parts.slice(2).join('.')
          if (!byEventType[eventType]) {
            byEventType[eventType] = { total: 0 }
          }
          if (field === 'total') {
            byEventType[eventType].total = value as number
          } else if (field.startsWith('byTarget.')) {
            const targetKey = field.replace('byTarget.', '')
            byEventType[eventType].byTarget = byEventType[eventType].byTarget ?? {}
            byEventType[eventType].byTarget![targetKey] = value as number
          } else if (field.startsWith('byType.')) {
            const typeKey = field.replace('byType.', '')
            byEventType[eventType].byType = byEventType[eventType].byType ?? {}
            byEventType[eventType].byType![typeKey] = value as number
          } else if (field.startsWith('terms.')) {
            const term = field.replace('terms.', '')
            byEventType[eventType].terms = byEventType[eventType].terms ?? {}
            byEventType[eventType].terms![term] = value as number
          }
        }
      }

      set({
        eventStats: {
          byEventType,
          lastUpdatedAt: data.lastUpdatedAt?.toDate() ?? null,
        },
        loading: false,
      })
    } catch (err) {
      set({ error: (err as Error).message, loading: false })
    }
  },
}))
